/**
 * AnalysisTabs — Systeme de 7 onglets d'analyse astrologique
 * Design reproduit pixel-perfect depuis client-view.php
 */

import { useState, useCallback } from 'react';
import { colors } from '../../styles/tokens.js';
import type { ChartAnalysis } from '../../types/clients.js';
import { apiFetch } from '../../utils/api.js';

import { PositionsTab } from './PositionsTab.js';
import { SignatureTab } from './SignatureTab.js';
import { StructureTab } from './StructureTab.js';
import { AspectsTab } from './AspectsTab.js';
import { ConfigurationsTab } from './ConfigurationsTab.js';
import { AxeNodalTab } from './AxeNodalTab.js';
import { DignitesTab } from './DignitesTab.js';
import { SyntheseTab } from './SyntheseTab.js';

type TabId = 'positions' | 'signature' | 'structure' | 'aspects' | 'configurations' | 'axe-nodal' | 'dignites' | 'synthese';

/** Types planetaires — descriptions courtes issues des cours */
const TYPES_PLANETAIRES: Record<string, string> = {
  'Soleil': "Personnalité solaire : besoin de reconnaissance et d'expression créative. Passionné, loyal, ambitieux, volontaire.",
  'Lune': "Personnalité lunaire : réceptive et accueillante, besoin de sérénité et de liens affectifs.",
  'Mercure': "Personnalité mercurienne : curiosité intellectuelle toujours en éveil, besoin de communiquer et de multiplier les centres d'intérêt.",
  'Vénus': "Personnalité vénusienne : sociable, tendre, sensible à la beauté et à l'harmonie. Sensibilité artistique marquée.",
  'Mars': "Personnalité martienne : réalisateur dynamique, impatient d'agir. Courageux, direct, combatif.",
  'Jupiter': "Personnalité jupitérienne : optimiste, expansif, bon vivant. Qualités de meneur et d'enseignant.",
  'Saturne': "Personnalité saturnienne : patiente, prudente, sceptique. Philosophe dans l'âme, réactions secondaires.",
  'Uranus': "Personnalité uranienne : idéaliste, cérébrale, visionnaire. Affirme sa singularité et son originalité.",
  'Neptune': "Personnalité neptunienne : intuitive, sentimentale, branchée sur les réalités invisibles. Sensibilité médiumnique.",
  'Pluton': "Personnalité plutonienne : en contact avec la profonde complexité des êtres. Mystérieux, lucide, intuitions pénétrantes.",
};

interface TabDef {
  id: TabId;
  label: string;
  icon: string;
}

const TABS: TabDef[] = [
  { id: 'positions',      label: 'Positions',      icon: 'bi-globe' },
  { id: 'signature',      label: 'Signature',      icon: 'bi-lightning-charge-fill' },
  { id: 'structure',      label: 'Structure',       icon: 'bi-grid-3x3-gap-fill' },
  { id: 'aspects',        label: 'Aspects',         icon: 'bi-diagram-3-fill' },
  { id: 'configurations', label: 'Configurations',  icon: 'bi-diagram-2-fill' },
  { id: 'axe-nodal',      label: 'Axe Nodal',       icon: 'bi-arrow-left-right' },
  { id: 'dignites',       label: 'Dignites',        icon: 'bi-award-fill' },
  { id: 'synthese',       label: 'Synthese',        icon: 'bi-journal-text' },
];

interface Props {
  analysis: ChartAnalysis;
  importDate?: string;
  clientId: number;
  chartId: number;
  onAnalysisUpdate?: (analysis: ChartAnalysis) => void;
}

export function AnalysisTabs({ analysis, importDate, clientId, chartId, onAnalysisUpdate }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('positions');
  const [saving, setSaving] = useState(false);
  const [showDominantesHelp, setShowDominantesHelp] = useState(false);

  /** Bascule la selection d'une dominante et sauvegarde en base */
  const toggleDominante = useCallback(async (planete: string) => {
    const current = analysis.dominantesSelectionnees ?? [];
    const next = current.includes(planete)
      ? current.filter((p) => p !== planete)
      : [...current, planete];

    // Mise a jour optimiste
    if (onAnalysisUpdate) {
      onAnalysisUpdate({ ...analysis, dominantesSelectionnees: next });
    }

    setSaving(true);
    try {
      await apiFetch(`/clients/${clientId}/charts/${chartId}/dominantes`, {
        method: 'PATCH',
        body: JSON.stringify({ selectionnees: next }),
      });
    } catch {
      // Rollback en cas d'erreur
      if (onAnalysisUpdate) {
        onAnalysisUpdate({ ...analysis, dominantesSelectionnees: current });
      }
    } finally {
      setSaving(false);
    }
  }, [analysis, clientId, chartId, onAnalysisUpdate]);

  return (
    <div>
      {/* Barre d'onglets — style PHP (border-bottom, pas pills) */}
      <div style={{ marginBottom: '1.5rem' }}>
        <nav style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0,
          borderBottom: `2px solid ${colors.border}`,
        }}>
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: `3px solid ${active ? colors.accent : 'transparent'}`,
                  marginBottom: -2,
                  fontSize: '0.85rem',
                  color: active ? colors.accent : colors.textMid,
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.color = colors.accent;
                    e.currentTarget.style.background = colors.bgPage;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.color = colors.textMid;
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <i className={tab.icon} style={{ fontSize: '0.9rem' }} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu de l'onglet actif */}
      {activeTab === 'positions' && analysis.planetes && (
        <>
          {/* Dominantes planétaires — sélection manuelle par l'astrologue */}
          {analysis.dominantes && analysis.dominantes.length > 0 && (() => {
            const candidates = analysis.dominantes!.filter((d) => d.niveau !== 4);
            const sousDominantes = analysis.dominantes!.filter((d) => d.niveau === 4);
            const selectionnees = analysis.dominantesSelectionnees ?? [];
            const retenues = candidates.filter((d) => selectionnees.includes(d.planete));
            const nonRetenues = candidates.filter((d) => !selectionnees.includes(d.planete));

            /** Rendu d'une ligne de planete candidate */
            function renderPlanete(d: typeof candidates[0], isRetenue: boolean) {
              const selected = selectionnees.includes(d.planete);
              return (
                <div key={d.planete} style={{ opacity: isRetenue ? 1 : 0.7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', minWidth: 95 }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={saving}
                        onChange={() => toggleDominante(d.planete)}
                        style={{ width: 16, height: 16, accentColor: '#f59e0b', cursor: 'pointer' }}
                      />
                      <strong style={{ color: isRetenue ? '#92400e' : '#78716c' }}>{d.planete}</strong>
                    </label>
                    {d.score > 0 && (
                      <span style={{
                        fontSize: '0.75rem', padding: '2px 8px', borderRadius: 10,
                        background: isRetenue ? '#fef3c7' : '#f5f5f4', color: isRetenue ? '#92400e' : '#78716c', fontWeight: 700,
                      }}>
                        {d.score} critere{d.score > 1 ? 's' : ''}
                      </span>
                    )}
                    {d.lente && (
                      <span style={{
                        fontSize: '0.75rem', padding: '1px 8px', borderRadius: 10,
                        background: isRetenue ? '#fef3c7' : '#f5f5f4', color: isRetenue ? '#92400e' : '#78716c', fontStyle: 'italic',
                      }}>
                        planete lente
                      </span>
                    )}
                  </div>
                  {/* Liste des criteres valides */}
                  {d.criteres && d.criteres.length > 0 && (
                    <ul style={{
                      margin: '4px 0 0', paddingLeft: 105, listStyle: 'none',
                      fontSize: '0.8rem', color: isRetenue ? '#78350f' : '#a8a29e',
                    }}>
                      {d.criteres.map((c, j) => (
                        <li key={j} style={{ lineHeight: 1.5 }}>
                          <span style={{ color: isRetenue ? '#f59e0b' : '#d6d3d1', marginRight: 4 }}>▸</span>
                          {c}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Type planetaire — uniquement pour les retenues */}
                  {isRetenue && TYPES_PLANETAIRES[d.planete] && (
                    <div style={{
                      fontSize: '0.8rem', color: '#a16207', fontStyle: 'italic',
                      marginTop: 4, paddingLeft: 105,
                    }}>
                      {TYPES_PLANETAIRES[d.planete]}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <>
                {/* En-tete avec bouton Aide */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: '0.75rem',
                }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
                    <i className="bi bi-star-fill" style={{ color: '#f59e0b', marginRight: 8 }} />
                    Dominante planetaire
                  </h3>
                  <button
                    onClick={() => setShowDominantesHelp(!showDominantesHelp)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
                      background: showDominantesHelp ? colors.accent : '#f3f4f6',
                      color: showDominantesHelp ? '#fff' : colors.textMid,
                      border: 'none', borderRadius: 6, cursor: 'pointer',
                      transition: 'all 0.2s', fontFamily: 'inherit',
                    }}
                  >
                    <i className="bi bi-question-circle" />
                    Aide
                  </button>
                </div>

                {/* Panneau d'aide (toggle) */}
                {showDominantesHelp && (
                  <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
                    padding: '1.25rem', marginBottom: '0.75rem', fontSize: '0.85rem',
                    lineHeight: 1.7, color: '#1e3a5f',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
                        Comprendre la dominante planetaire
                      </h4>
                      <button
                        onClick={() => setShowDominantesHelp(false)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '1.2rem', color: '#64748b', lineHeight: 1,
                        }}
                      >
                        &times;
                      </button>
                    </div>

                    <p style={{ margin: '0 0 8px' }}>
                      La <strong>dominante planetaire</strong> est la planete qui colore le plus la personnalite du natif.
                      C'est elle qui donne la "tonalite" fondamentale du theme.
                    </p>

                    <p style={{ fontWeight: 700, margin: '0 0 4px' }}>Criteres de detection :</p>
                    <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                      <li><strong>Conjonction aux luminaires</strong> (Soleil/Lune) — orbe 8° (10° pour les planetes lentes)</li>
                      <li><strong>Conjonction aux angles</strong> (AS, MC, DS, FC) — orbe 8°</li>
                      <li><strong>Focus d'une configuration majeure</strong> — apex d'un Yod ou d'un T-Carre</li>
                      <li><strong>Conjonction a Mercure ou Venus</strong> — orbe 8°</li>
                      <li><strong>Conjonction au maitre de l'Ascendant</strong> — orbe 8°</li>
                      <li><strong>Maitresse d'un amas planetaire</strong> — stellium ou concentration</li>
                    </ul>

                    <p style={{ margin: '0 0 8px' }}>
                      Plus une planete <strong>cumule de criteres</strong>, plus elle est significative.
                      Les <strong>planetes lentes</strong> (Jupiter a Pluton) renforcent la dominante quand elles sont conjointes a un luminaire.
                    </p>

                    <p style={{ margin: 0, fontStyle: 'italic' }}>
                      Le classement est automatique, mais c'est l'astrologue qui tranche :
                      cochez les planetes que vous retenez comme dominantes pour ce theme.
                    </p>
                  </div>
                )}

                {/* Dominantes retenues (selectionnees par l'astrologue) */}
                {retenues.length > 0 && (
                  <div style={{
                    background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 10,
                    padding: '1rem 1.25rem', marginBottom: '0.75rem',
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 700, color: '#92400e' }}>
                      <i className="bi bi-star-fill" style={{ marginRight: 6, color: '#f59e0b' }} />
                      Dominante{retenues.length > 1 ? 's' : ''} retenue{retenues.length > 1 ? 's' : ''}
                    </h4>
                    <div style={{ display: 'grid', gap: '0.8rem' }}>
                      {retenues.map((d) => renderPlanete(d, true))}
                    </div>
                  </div>
                )}

                {/* Candidates non retenues (classees par score) */}
                {nonRetenues.length > 0 && (
                  <div style={{
                    background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 10,
                    padding: '0.75rem 1.25rem', marginBottom: sousDominantes.length > 0 ? '0.75rem' : '1.25rem',
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 600, color: '#78716c' }}>
                      <i className="bi bi-list-check" style={{ marginRight: 6, color: '#a8a29e' }} />
                      Candidates{retenues.length > 0 ? ' non retenues' : ' — cocher pour retenir'}
                    </h4>
                    <div style={{ display: 'grid', gap: '0.6rem' }}>
                      {nonRetenues.map((d) => renderPlanete(d, false))}
                    </div>
                  </div>
                )}

                {/* Sous-dominantes (niveau 4 — conjonction aux Noeuds) */}
                {sousDominantes.length > 0 && (
                  <div style={{
                    background: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: 10,
                    padding: '0.75rem 1.25rem', marginBottom: '1.25rem',
                  }}>
                    <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', fontWeight: 600, color: '#6d28d9' }}>
                      <i className="bi bi-arrow-left-right" style={{ marginRight: 6, color: '#8b5cf6' }} />
                      Sous-dominante{sousDominantes.length > 1 ? 's' : ''}
                    </h4>
                    <div style={{ display: 'grid', gap: '0.3rem' }}>
                      {sousDominantes.map((d, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                          <strong style={{ color: '#6d28d9', minWidth: 70 }}>{d.planete}</strong>
                          <span style={{ color: '#7c3aed' }}>{d.critere}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          <PositionsTab planetes={analysis.planetes} importDate={importDate} />
        </>
      )}
      {activeTab === 'signature' && (
        <SignatureTab signature={analysis.signature} />
      )}
      {activeTab === 'structure' && analysis.structureSpatiale ? (
        <>
          {/* Dessin planétaire — encadré en haut de l'onglet Structure */}
          {analysis.dessinPlanetaire && (
            <div style={{
              background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 10,
              padding: '1rem 1.25rem', marginBottom: '1.25rem',
            }}>
              <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 700, color: '#0c4a6e' }}>
                <i className="bi bi-diagram-3" style={{ marginRight: 6, color: '#0ea5e9' }} />
                Dessin planétaire : {analysis.dessinPlanetaire.type}
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#164e63', lineHeight: 1.6 }}>
                {analysis.dessinPlanetaire.description}
              </p>
            </div>
          )}
          <StructureTab structure={analysis.structureSpatiale} />
        </>
      ) : activeTab === 'structure' ? (
        <div style={{ fontSize: 14, color: colors.textMuted }}>Donnees de structure spatiale non disponibles.</div>
      ) : null}
      {activeTab === 'aspects' && (
        <AspectsTab aspects={analysis.aspects} />
      )}
      {activeTab === 'configurations' && (
        <ConfigurationsTab configurations={analysis.configurations} retroGenerationnels={analysis.retroGenerationnels} />
      )}
      {activeTab === 'axe-nodal' && analysis.axeNodal ? (
        <AxeNodalTab axeNodal={analysis.axeNodal} />
      ) : activeTab === 'axe-nodal' ? (
        <div style={{ fontSize: 14, color: colors.textMuted }}>Donnees de l'axe nodal non disponibles.</div>
      ) : null}
      {activeTab === 'dignites' && analysis.dignites ? (
        <DignitesTab dignites={analysis.dignites} />
      ) : activeTab === 'dignites' ? (
        <div style={{ fontSize: 14, color: colors.textMuted }}>Donnees de dignites non disponibles.</div>
      ) : null}
      {activeTab === 'synthese' && (
        <SyntheseTab analysis={analysis} />
      )}
    </div>
  );
}
